import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material';
import {
  Settings,
  Cpu,
  Home
} from 'lucide-react';
import axios from 'axios';
import ServiceCard from './ServiceCard';
import { fadeInUp } from '../styles/sharedStyles';
import ErrorBoundary from './ErrorBoundary';

const ServicesOverview = ({ status, loading, token, onlyTrueNAS = false, onlyRpi = false, onlyHA = false }) => {
  const [tabValue, setTabValue] = useState(onlyRpi ? 1 : (onlyHA ? 2 : 0));
  const [notificationSettings, setNotificationSettings] = useState({});

  const fetchNotificationSettings = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get('/api/services/settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationSettings(response.data);
    } catch (err) {
      console.error('Error fetching service notification settings:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchNotificationSettings();
  }, [fetchNotificationSettings]);

  const handleToggleNotifications = async (serviceName, enabled) => {
    if (!token) return;
    try {
      await axios.put(`/api/services/${serviceName}/notifications`, 
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotificationSettings(prev => ({
        ...prev,
        [serviceName]: enabled
      }));
    } catch (err) {
      console.error('Error updating notification setting:', err);
    }
  };

  const truenasServices = [
    { name: 'truenas', display_name: 'TrueNAS', url: 'https://truenas.raidnet.pl/ui/signin', is_online: status?.truenas?.is_online },
    { name: 'webdav', display_name: 'WebDAV', url: '', is_online: status?.services?.find(s => s.name === 'webdav')?.is_online },
    { name: 'ddns_updater', display_name: 'DDNS Updater', url: 'http://192.168.8.8:30007/', is_online: status?.services?.find(s => s.name === 'ddns_updater')?.is_online },
    { name: 'jellyfin', display_name: 'Jellyfin', url: '', is_online: status?.services?.find(s => s.name === 'jellyfin')?.is_online },
    { name: 'immich', display_name: 'Immich', url: '', is_online: status?.services?.find(s => s.name === 'immich')?.is_online },
    { name: 'gramps', display_name: 'Gramps', url: '', is_online: status?.services?.find(s => s.name === 'gramps')?.is_online },
    { name: 'nextcloud', display_name: 'Nextcloud', url: '', is_online: status?.services?.find(s => s.name === 'nextcloud')?.is_online },
    { name: 'twofactor_auth', display_name: '2FAuth', url: 'https://2fa.raidnet.pl/', is_online: status?.services?.find(s => s.name === 'twofactor_auth')?.is_online },
  ];

  // Update with actual status from backend if available
  const mergeServiceStatus = (serviceList) => {
    return serviceList.map(s => {
      // Special handling for TrueNAS server itself
      if (s.name === 'truenas') {
        return {
          ...s,
          is_online: status?.truenas?.is_online,
          response_time: status?.truenas?.response_time,
          last_checked: status?.truenas?.last_seen,
          url: s.url
        };
      }

      const backendService = status?.services?.find(bs => bs.name.toLowerCase() === s.name.toLowerCase());
      return {
        ...s,
        is_online: backendService ? backendService.is_online : s.is_online,
        response_time: backendService?.response_time,
        last_checked: backendService?.last_checked,
        url: s.url || backendService?.url || ''
      };
    });
  };

  const rpiServices = [
    { name: 'cockpit', display_name: 'Cockpit', url: 'https://192.168.0.10:9090', is_online: true },
    { name: 'adguard', display_name: 'AdGuard', url: 'http://192.168.0.10', is_online: true },
    { name: 'dockge', display_name: 'Dockge', url: 'http://192.168.0.10:5001', is_online: true },
    { name: 'qbit', display_name: 'Qbit', url: 'http://192.168.0.10:8080', is_online: true },
  ];

  const haServices = [
    { name: 'home_assistant', display_name: 'Home Assistant', url: '', is_online: status?.services?.find(s => s.name === 'home_assistant')?.is_online },
    { name: 'nginx_proxy_manager', display_name: 'Nginx Proxy Manager', url: 'http://192.168.0.100:81', is_online: true },
  ];

  const getActiveServices = () => {
    switch (tabValue) {
      case 0: return mergeServiceStatus(truenasServices);
      case 1: return mergeServiceStatus(rpiServices);
      case 2: return mergeServiceStatus(haServices);
      default: return [];
    }
  };

  // Sort: online first, then offline; within each group A–Z by display name
  const sortServices = (services) => {
    return [...services].sort((a, b) => {
      if (a.is_online !== b.is_online) return a.is_online ? -1 : 1;
      const nameA = (a.display_name || a.name || '').toLowerCase();
      const nameB = (b.display_name || b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  return (
    <Box>
      {!onlyTrueNAS && !onlyRpi && !onlyHA && (
        <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': {
                background: (theme) => theme.palette.primary.main,
                height: '3px'
              },
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.6)',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                '&.Mui-selected': { color: (theme) => theme.palette.primary.main },
                '& svg': { color: 'inherit' }
              }
            }}
          >
            <Tab icon={<Settings size={18} />} iconPosition="start" label="TrueNAS" />
            <Tab icon={<Cpu size={18} />} iconPosition="start" label="Debian 13" />
            <Tab icon={<Home size={18} />} iconPosition="start" label="HAOS" />
          </Tabs>
        </Box>
      )}

      <Box>
        <ErrorBoundary>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#00d4ff' }} />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {sortServices(getActiveServices()).map((service, index) => (
                <Grid item xs={12} sm={6} lg={4} xl={3} key={service.name}>
                  <Box
                    sx={{
                      animation: `${fadeInUp} 0.6s ease-out`,
                      animationDelay: `${index * 0.1}s`,
                      animationFillMode: 'both'
                    }}
                  >
                    <ServiceCard 
                      service={service} 
                      notificationsEnabled={notificationSettings[service.name] !== false}
                      onToggleNotifications={handleToggleNotifications}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </ErrorBoundary>
      </Box>
    </Box>
  );
};

export default ServicesOverview;
